import { useRef, useState } from "react";
import { Star, Volume2, VolumeX } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const STORIES = [
  {
    id: "bash",
    video: "/videos/bash.mp4",
    quote: "Bash has never once sat still for me — yet there he is, still forever on that canvas. This isn't Bash's pose. It's Bash's presence.",
    name: "Bash's family",
    pet: "Bash · commissioned original",
  },
  {
    id: "mittens",
    video: "/videos/mittens.mp4",
    quote: "Fourteen years of attitude, captured in a single glance. Mittens finally has the portrait her ego always demanded — and it exceeds even her expectations.",
    name: "Mittens' family",
    pet: "Mittens · commissioned original",
  },
  {
    id: "shababy",
    video: "/videos/shababy.mp4",
    quote: "I sent photos and told stories, and what came back wasn't a likeness — it was Shababy herself. I cried before I even got the tape off the box.",
    name: "Shababy's family",
    pet: "Shababy · commissioned original",
  },
];

const StoryCard = ({ story, index }) => {
  const videoRef = useRef(null);
  const [muted, setMuted] = useState(true);

  const toggleSound = () => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setMuted(v.muted);
    if (!v.muted) v.play().catch(() => {});
  };

  return (
    <Reveal delay={0.12 + index * 0.12}>
      <figure
        data-testid={`testimonial-card-${story.id}`}
        className="h-full rounded-2xl bg-[#1F1B17] border border-[#2E2721] hover:border-[#41C2F2]/40 transition-colors duration-500 overflow-hidden"
      >
        <div className="relative aspect-[4/5] bg-[#0C0A09] overflow-hidden group">
          <video
            ref={videoRef}
            data-testid={`story-video-${story.id}`}
            src={story.video}
            className="w-full h-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
          />
          <button
            data-testid={`story-sound-${story.id}`}
            onClick={toggleSound}
            aria-label={muted ? "Unmute video" : "Mute video"}
            className="absolute bottom-3 right-3 p-2.5 rounded-full bg-[#0C0A09]/75 border border-stone-700 text-stone-200 hover:text-[#41C2F2] hover:border-[#41C2F2] transition-colors opacity-90 group-hover:opacity-100"
          >
            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
        </div>
        <div className="p-7">
          <div className="flex gap-1" aria-label="5 star rating">
            {[...Array(5)].map((_, j) => (
              <Star key={j} className="w-4 h-4 fill-[#41C2F2] text-[#41C2F2]" />
            ))}
          </div>
          <blockquote className="mt-4 font-serif text-lg leading-relaxed text-stone-200 italic">
            "{story.quote}"
          </blockquote>
          <figcaption className="mt-6 pt-5 border-t border-stone-800">
            <p className="text-sm font-semibold text-[#EAF5FA]">{story.name}</p>
            <p className="text-xs text-stone-500 mt-0.5">{story.pet}</p>
          </figcaption>
        </div>
      </figure>
    </Reveal>
  );
};

export const Testimonials = () => (
  <section id="stories" data-testid="testimonials-section" className="relative py-28 sm:py-36">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <Reveal><Eyebrow>Client Stories</Eyebrow></Reveal>
      <Reveal delay={0.1}>
        <h2 className="mt-5 max-w-2xl font-serif tracking-tight leading-[1.08] text-3xl sm:text-4xl lg:text-5xl text-[#EAF5FA]">
          The moment of the <span className="gold-foil-text italic">unboxing</span>
        </h2>
      </Reveal>

      <div className="mt-16 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {STORIES.map((s, i) => (
          <StoryCard key={s.id} story={s} index={i} />
        ))}
      </div>
    </div>
  </section>
);
